import { AdData } from "../api/api-types";
import { Ad } from "../public-types";
import { createLogger, Logger } from "../utils/log";
import {
  convertTrackingEventsAndImpressionsToMap,
  trackEvent,
  type TrackingEventsMap,
  TrackingEventsMapKey,
} from "./tracking-events";

export type AdCallbacks = {
  onAdStart: (ad: Ad) => void;
  onAdEnd: (ad: Ad) => void;
};

type PrismAdOptions = {
  adData: AdData;
  callbacks: AdCallbacks;
};

type Quartile = 25 | 50 | 75;

export class PrismAd {
  private logger: Logger;

  private adData: AdData;
  private callbacks: AdCallbacks;

  private started = false;
  private watched = false;

  private quartilesTriggered: Quartile[] = [];
  private trackingEvents: TrackingEventsMap;

  constructor(options: PrismAdOptions) {
    this.logger = createLogger(`ad:${options.adData.title}`);

    this.adData = options.adData;
    this.callbacks = options.callbacks;

    this.trackingEvents = convertTrackingEventsAndImpressionsToMap(
      this.adData.trackingEvents,
      this.adData.impressions
    );
  }

  private get duration() {
    return this.adData.adEnd - this.adData.adStart;
  }

  private trackEvent(eventType: TrackingEventsMapKey) {
    if (this.watched) {
      // ignore events after ad is watched
      return;
    }
    this.logger.info(eventType);
    trackEvent(this.trackingEvents.get(eventType));
  }

  /**
   * Check if the quartiles are triggered and in the provided order.
   */
  private isQuartilesTriggered(quartiles: Quartile[]): boolean {
    return quartiles.join() === this.quartilesTriggered.join();
  }

  public isStreamPositionInAd(streamPosition: number): boolean {
    return (
      streamPosition >= this.adData.adStart &&
      streamPosition < this.adData.adEnd
    );
  }

  public handleStart() {
    if (this.started) {
      this.logger.warn("invalid handleStart, already started");
      return;
    }
    this.started = true;
    this.logger.info("start");
    this.callbacks.onAdStart(this.publicAd);

    this.trackEvent("impression");
  }

  public handlePause() {
    if (!this.started) {
      this.logger.warn(`invalid handlePause, not started`);
      return;
    }
    this.trackEvent("pause");
  }

  public handleResume() {
    if (!this.started) {
      this.logger.warn("invalid handleResume, not started");
      return;
    }
    this.logger.info("resume");
    this.trackEvent("resume");
  }

  public handleStreamPositionUpdate(streamPosition: number) {
    if (!this.started) {
      this.logger.warn("invalid handleStreamPositionUpdate, not started");
      return;
    }
    if (!this.isStreamPositionInAd(streamPosition)) {
      this.logger.warn(
        `invalid handleStreamPositionUpdate, not within the stream position: ${streamPosition}`
      );
      return;
    }

    const adPosition = streamPosition - this.adData.adStart;
    const percentagePlayed = (adPosition / this.duration) * 100;

    if (percentagePlayed >= 25 && this.isQuartilesTriggered([])) {
      this.quartilesTriggered.push(25);

      this.trackEvent("firstQuartile");
    }
    if (percentagePlayed >= 50 && this.isQuartilesTriggered([25])) {
      this.quartilesTriggered.push(50);

      this.trackEvent("midpoint");
    }
    if (percentagePlayed >= 75 && this.isQuartilesTriggered([25, 50])) {
      this.quartilesTriggered.push(75);

      this.trackEvent("thirdQuartile");
    }
  }

  public handleEnd() {
    if (!this.started) {
      this.logger.warn(`invalid handleEnd, ad not started`);
      return;
    }
    this.started = false;
    this.callbacks.onAdEnd(this.publicAd);

    if (!this.isQuartilesTriggered([25, 50, 75])) {
      this.logger.warn(
        `ad not complete, not all quartiles triggered. quartiles: ${this.quartilesTriggered.join(", ")}`
      );
      return;
    }
    this.trackEvent("complete");

    this.watched = true;
  }

  public get publicAd(): Ad {
    return {
      start: this.adData.adStart,
      end: this.adData.adEnd,
      duration: this.adData.adEnd - this.adData.adStart,

      title: this.adData.title,
      universalAdId: this.adData.universalAdId,
      campaignId: this.adData.campaignId,
      adType: this.adData.adType,

      clickThrough: this.adData.clickThrough?.url,
      clickTracking: this.adData.clickTracking?.map((click) => click.url),
    };
  }
}
